import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router";
import styled from "styled-components";
import qs from "querystring";

import { fetchWorkspaceStats } from "../../actions/stats";

import KpiSection from "./KpiSection";
import TrendsHeader from "./TrendsHeader";
import RecommendationsSection from "./RecommendationsSection";
import IndexabilitySection from "./IndexabilitySection";
import { readDateRangeFromSearch, numDaysToToday } from "./utils";
import { useGetStatsHistoryQuery } from "./state/api";
import ErrorsSection from "./ErrorSection";
import ScopeSection from "./ScopeSection";
import WorkspaceHeader from "../common/WorkspaceHeader";

const ChartGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    margin-top: 16px;
    @media (max-width: 640px)  { grid-template-columns: 1fr; }
`;

export default function TrendsDashboard() {
    const dispatch = useDispatch();
    const curWorkspace = useSelector((s) => s.workspaces?.current);
    const { workspaceNumber } = useParams();

    // Live stats power the KPI-tile headline numbers (the charts/sparklines use history).
    useEffect(() => {
        if (workspaceNumber != null) dispatch(fetchWorkspaceStats(workspaceNumber));
    }, [dispatch, workspaceNumber]);

    const { search } = useLocation();
    const { startIso, endIso } = readDateRangeFromSearch(search);

    // An explicit `?start=&end=` in the URL means the user picked exact bounds — render them
    // as asked, even if the right edge runs past where we have data. A `?range=Xm` preset or
    // the default fall through to a snapshot-clamped end so the chart doesn't show a void.
    const query = qs.parse((search || "").replace(/^\?/, ""));
    const isExplicitCustom = typeof query.start === "string" && typeof query.end === "string";

    // Fetch the same window the children will fetch (same RTKQ cache key → single network call).
    const size = numDaysToToday({ startIso });
    const { data: snapshots } = useGetStatsHistoryQuery(
        { workspaceNumber, size },
        { skip: workspaceNumber == null }
    );

    const lastSnapshotIso = useMemo(() => {
        if (!Array.isArray(snapshots) || snapshots.length === 0) return null;
        let max = null;
        for (const s of snapshots) {
            if (s?.timestamp && (!max || s.timestamp > max)) max = s.timestamp;
        }
        return max;
    }, [snapshots]);

    const effectiveEndIso = isExplicitCustom
        ? endIso
        : (lastSnapshotIso || endIso);

    if (!curWorkspace) return null;

    return (
        <>
            <WorkspaceHeader
                    name={curWorkspace.name}
                    url={curWorkspace.websiteUrl}
                    countryCode={curWorkspace.countryCode}
                />

                <TrendsHeader workspaceNumber={workspaceNumber} effectiveEndIso={effectiveEndIso} />

                <KpiSection startDate={startIso} endDate={effectiveEndIso} />

                <ChartGrid>
                    <ScopeSection startDate={startIso} endDate={effectiveEndIso} />
                    <ErrorsSection startDate={startIso} endDate={effectiveEndIso} />
                    <IndexabilitySection startDate={startIso} endDate={effectiveEndIso} />
                    <RecommendationsSection startDate={startIso} endDate={effectiveEndIso} />
                </ChartGrid>
        </>
    );
}
